// Browsers fire "ResizeObserver loop …" as a window error whenever an observer
// driven layout needs a second pass — a device rotation, the iOS URL bar
// collapsing, a 3D canvas remeasuring. It is benign and self-correcting, but the
// trap below wipes the page for ANY window error, so without this bypass a
// customer looking at their design on a phone gets a red stack trace instead.
const isBenignResizeObserverError = (msg: string) =>
    msg.includes("ResizeObserver loop completed with undelivered notifications") ||
    msg.includes("ResizeObserver loop limit exceeded");

// HARD RESET DIAGNOSTIC: Global Error Trap (No React)
window.addEventListener("error", (e) => {
    const errorMsg = e.error?.message || e.message || "";
    const errorStack = e.error?.stack || "";
    if (isBenignResizeObserverError(errorMsg)) {
        e.preventDefault();
        return;
    }
    if (errorMsg.includes("_leaflet_pos") || errorMsg.includes("leaflet") || errorStack.includes("_leaflet_pos") || errorStack.includes("leaflet")) {
        console.warn("[Leaflet Bypass] Suppressed background leaflet error:", e.error || e.message);
        e.preventDefault(); // Actually prevent the error from propagating
        return;
    }
    document.body.innerHTML =
        "<pre style='padding:16px;white-space:pre-wrap;color:red;font-size:16px;background:white;z-index:99999;position:fixed;top:0;left:0;width:100%;height:100%'>" +
        "GLOBAL ERROR:\n" +
        (e.error?.stack || e.message) +
        "</pre>";
});

window.addEventListener("unhandledrejection", (e: any) => {
    const reasonStr = String(e.reason || "");
    const reasonStack = e.reason?.stack || "";
    if (isBenignResizeObserverError(reasonStr)) {
        e.preventDefault();
        return;
    }
    if (reasonStr.includes("_leaflet_pos") || reasonStr.includes("leaflet") || reasonStack.includes("_leaflet_pos") || reasonStack.includes("leaflet")) {
        console.warn("[Leaflet Bypass] Suppressed background leaflet unhandled promise rejection:", e.reason);
        e.preventDefault(); // Actually prevent the rejection from propagating
        return;
    }
    document.body.innerHTML =
        "<pre style='padding:16px;white-space:pre-wrap;color:red;font-size:16px;background:white;z-index:99999;position:fixed;top:0;left:0;width:100%;height:100%'>" +
        "UNHANDLED PROMISE:\n" +
        (e.reason?.stack || String(e.reason)) +
        "</pre>";
});

import React from "react";
import "leaflet/dist/leaflet.css";
import { createRoot } from "react-dom/client";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./lib/queryClient";
import "./index.css";
import { startVersionWatch } from "./lib/version-watch";

class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean; error: Error | null }> {
    constructor(props: { children: React.ReactNode }) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error) {
        // Silently ignore Leaflet errors
        const errorMsg = error?.message || "";
        const errorStack = error?.stack || "";
        if (errorMsg.includes("_leaflet_pos") || errorMsg.includes("leaflet") || errorStack.includes("_leaflet_pos") || errorStack.includes("leaflet")) {
            console.warn("[Leaflet Error Boundary] Ignored leaflet error:", error);
            return { hasError: false, error: null }; // Don't set hasError for Leaflet errors
        }
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        // Don't log Leaflet errors
        const errorMsg = error?.message || "";
        const errorStack = error?.stack || "";
        if (!(errorMsg.includes("_leaflet_pos") || errorMsg.includes("leaflet") || errorStack.includes("_leaflet_pos") || errorStack.includes("leaflet"))) {
            console.error("Uncaught error:", error, errorInfo);
        }
    }

    render() {
        if (this.state.hasError) {
            return (
                <div style={{ padding: "20px", color: "red", fontFamily: "monospace", wordBreak: "break-all" }}>
                    <h1>Something went wrong.</h1>
                    <pre>{this.state.error?.toString()}</pre>
                    <pre>{this.state.error?.stack}</pre>
                </div>
            );
        }

        return this.props.children;
    }
}

const rootElement = document.getElementById("root");

// The public TradeVault landing page loads without the CRM: App imports every
// CRM page up front (~9MB), which made the page crawl on phones. See
// tradevault-landing-entry.tsx.
const isLandingPage = /^\/tradevault\/?$/.test(window.location.pathname);

function loadLandingEntry() {
    return import("./tradevault-landing-entry");
}

function loadAppEntry() {
    return import("./App");
}

if (rootElement) {
    const root = createRoot(rootElement);
    // The two import()s live in separate functions on purpose: in one expression
    // the minifier folds them into a ternary that shares a single preload list,
    // and the landing page then fetches App anyway.
    const loadEntry = isLandingPage ? loadLandingEntry : loadAppEntry;
    loadEntry().then(({ default: Entry }) => {
        root.render(
            <ErrorBoundary>
                <QueryClientProvider client={queryClient}>
                    <Entry />
                </QueryClientProvider>
            </ErrorBoundary>
        );
    }).catch((err) => {
        // A deploy between loading index.html and fetching the chunk leaves the
        // old hash 404ing. One reload picks up the new index; the flag stops a
        // genuinely broken build from looping.
        if (!sessionStorage.getItem("entry-reloaded")) {
            sessionStorage.setItem("entry-reloaded", "1");
            window.location.reload();
            return;
        }
        throw err;
    });
} else {
    console.error("CRITICAL: Root element not found!");
}

// PWA Service Worker Registration.
// Safe to run: sw.js has NO fetch listener (it does not intercept or cache network
// requests), so it cannot serve stale content — it only clears old caches on activate
// and handles push notifications. This is what made it safe to re-enable after the
// earlier "response is null" fetch-handler bug that forced it off.
if ("serviceWorker" in navigator) {
    window.addEventListener("load", () => {
        navigator.serviceWorker.register("/sw.js").then((registration) => {
            console.log("[PWA] ServiceWorker registered with scope:", registration.scope);
            // Proactively check for a newer service worker on each load so an old
            // cache-clearing SW from a previous deploy gets replaced promptly.
            registration.update?.();
        }).catch((error) => {
            console.error("[PWA] ServiceWorker registration failed:", error);
        });
    });
}

// Warn the user (without ever force-reloading) when a newer app build has been deployed,
// so nobody keeps running stale code in a long-lived tab. See lib/version-watch.
startVersionWatch();

